/**
 * Foto de perfil completa e reutilizável (pedido do Hilton, 04/09/2026): o `PhotoUpload` do
 * paciente (seleção, câmera, girar, zoom, arrastar) mais tudo que cada tela repetia à mão —
 * carregar a foto atual como blob, recortar no tamanho do preview, salvar e remover.
 *
 * Quem usa entrega três funções e nada mais. O cadastro do paciente pode migrar para este
 * componente; o Meu perfil já nasce com ele.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import PhotoUpload from "./PhotoUpload";
import { refreshUserAvatar } from "./UserAvatar";
import { colors } from "../theme/designSystem";

export interface ProfilePhotoSource {
  /** Foto atual como blob; nulo quando não há. */
  load: () => Promise<Blob | null>;
  /** Grava a foto já recortada (JPEG 250×250). */
  save: (blob: Blob) => Promise<void>;
  remove: () => Promise<void>;
}

interface Props {
  source: ProfilePhotoSource;
  /** Bloqueia edição (ex.: registro ainda não salvo) e diz o motivo. */
  disabled?: boolean;
  disabledMessage?: string;
  /** Avisa a tela quando a foto muda no servidor (para atualizar avatar no header, por exemplo). */
  onSaved?: (hasPhoto: boolean) => void;
}

interface PhotoState {
  photo?: string;
  photoRotation?: number;
  photoZoom?: number;
  photoFlipX?: number;
  photoPositionX?: number;
  photoPositionY?: number;
}

const INITIAL: PhotoState = { photo: "", photoRotation: 0, photoZoom: 1, photoFlipX: 1, photoPositionX: 0, photoPositionY: 0 };

const ProfilePhoto: React.FC<Props> = ({ source, disabled = false, disabledMessage, onSaved }) => {
  const [state, setState] = useState<PhotoState>(INITIAL);
  const [serverHasPhoto, setServerHasPhoto] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const blob = await source.load();
      if (!blob) {
        setServerHasPhoto(false);
        setState(INITIAL);
        return;
      }
      const url = URL.createObjectURL(blob);
      setServerHasPhoto(true);
      setState({ ...INITIAL, photo: url });
    } catch {
      setError("Não foi possível carregar a foto.");
    }
  }, [source]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (data: PhotoState) => {
    setState((prev) => ({ ...prev, ...data }));
    setDirty(true);
    setError("");
  };

  // Mesmo recorte do cadastro do paciente: o que aparece no preview é o que vai para o servidor.
  const cropToBlob = (): Promise<Blob | null> =>
    new Promise((resolve) => {
      if (!state.photo) return resolve(null);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(null);
      const img = new Image();
      img.onload = () => {
        canvas.width = 250;
        canvas.height = 250;
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.translate(state.photoPositionX || 0, state.photoPositionY || 0);
        ctx.rotate(((state.photoRotation || 0) * Math.PI) / 180);
        ctx.scale(state.photoZoom || 1, (state.photoZoom || 1) * (state.photoFlipX || 1));
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
        ctx.restore();
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
      };
      img.onerror = () => resolve(null);
      img.src = state.photo!;
    });

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (state.photo) {
        const blob = await cropToBlob();
        if (!blob) throw new Error("Não foi possível processar a imagem.");
        await source.save(blob);
        refreshUserAvatar();
        setServerHasPhoto(true);
        onSaved?.(true);
      } else if (serverHasPhoto) {
        await source.remove();
        refreshUserAvatar();
        setServerHasPhoto(false);
        onSaved?.(false);
      }
      setDirty(false);
      await load();
    } catch (e) {
      const message =
        (e as { response?: { data?: { description?: string } } })?.response?.data?.description ||
        (e as Error)?.message ||
        "Não foi possível salvar a foto.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PhotoUpload
        photo={state.photo}
        photoRotation={state.photoRotation}
        photoZoom={state.photoZoom}
        photoFlipX={state.photoFlipX}
        photoPositionX={state.photoPositionX}
        photoPositionY={state.photoPositionY}
        disabled={disabled}
        disabledMessage={disabledMessage}
        onPhotoChange={handleChange}
      />

      {/* Só aparece quando há algo para salvar: uma foto nova ou a remoção da atual. */}
      {dirty && !disabled && (
        <Button
          fullWidth
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            mt: 1,
            backgroundColor: colors.primary,
            textTransform: "none",
            "&:hover": { backgroundColor: colors.primaryHover },
            "&.Mui-disabled": { backgroundColor: colors.primary, opacity: 0.7, color: "white" },
          }}
        >
          {saving ? "Salvando..." : state.photo ? "Salvar foto" : "Confirmar remoção"}
        </Button>
      )}

      {error && (
        <Typography sx={{ color: colors.error, fontSize: 13, mt: 0.5 }}>{error}</Typography>
      )}
    </Box>
  );
};

export default ProfilePhoto;
